import React, { useCallback, useEffect, useState } from "react";
import { api } from "../../lib/api";

import { logger } from "../../lib/log";
/**
 * Small pill showing today's try-on quota usage. Silently hides if the /usage
 * endpoint fails so it never blocks the studio. A window-level 'usage-refresh'
 * event triggers a live refetch (fired by Studio after a successful /tryon or
 * /runway so users see the meter tick up without a full page reload).
 */
export const UsageMeter = () => {
  const [usage, setUsage] = useState(null);

  const load = useCallback(() => {
    api.get("/usage/today")
      .then((r) => setUsage(r.data))
      .catch((e) => logger.warn("[usage] fetch failed", e?.message));
  }, []);

  useEffect(() => {
    load();
    const handler = () => load();
    window.addEventListener("usage-refresh", handler);
    return () => window.removeEventListener("usage-refresh", handler);
  }, [load]);

  if (!usage) return null;
  const { tryon, runway, tier } = usage;
  const tryonUnlimited = !tryon.cap || tryon.cap === 0;
  const runwayUnlimited = !runway.cap || runway.cap === 0;
  const tryonNearCap = !tryonUnlimited && tryon.used / tryon.cap >= 0.8;

  return (
    <div
      data-testid="usage-meter"
      className={`hidden md:flex items-center gap-3 text-[11px] font-mono-eyebrow px-3 py-1.5 border ${tryonNearCap ? "border-[color:var(--accent)] text-[color:var(--accent)]" : "border-black/15 text-black/60"}`}
    >
      <span data-testid="usage-tier">{tier === "paid" ? "Premium" : "Trial"}</span>
      <span className="opacity-40">·</span>
      <span data-testid="usage-tryon">
        Fittings {tryonUnlimited ? `${tryon.used} · Unlimited` : `${tryon.used}/${tryon.cap}`}
      </span>
      <span className="opacity-40">·</span>
      <span data-testid="usage-runway">
        Runway {runwayUnlimited ? `${runway.used} · Unlimited` : `${runway.used}/${runway.cap}`}
      </span>
    </div>
  );
};

/** Fire from anywhere that consumes quota to nudge the meter to refetch. */
export const refreshUsage = () => {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event("usage-refresh"));
  }
};
